import React, { useState, useEffect } from 'react';
import { getAlerts, markAlertRead } from '../api';
import { Bell, Warning, Info, CheckCircle, Eye } from '@phosphor-icons/react';
import { useTranslation } from '../i18n/LanguageContext';

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-alert-critical/10', border: 'border-alert-critical/30', text: 'text-alert-critical', icon: Warning },
  warning: { bg: 'bg-alert-warning/10', border: 'border-alert-warning/30', text: 'text-alert-warning', icon: Warning },
  info: { bg: 'bg-info/10', border: 'border-info/30', text: 'text-info', icon: Info },
};

export default function AlertsPanel({ industry }) {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const { data } = await getAlerts(industry);
        setAlerts(data.alerts || []);
      } catch (err) {
        console.error('Failed to load alerts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, [industry]);

  const handleMarkRead = async (title) => {
    try {
      await markAlertRead(title);
      setAlerts(prev => prev.map(a => a.title === title ? { ...a, read: true } : a));
    } catch (err) {
      console.error('Failed to mark alert read:', err);
    }
  };

  const unread = alerts.filter(a => !a.read);
  const read = alerts.filter(a => a.read);

  return (
    <div className="space-y-6" data-testid="alerts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
            {t('alerts.title')}
          </h1>
          <p className="font-body text-sm text-text-secondary mt-1">
            {t('alerts.unread', { count: unread.length, industry })}
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-alert-critical/10 rounded-full">
          <Bell size={16} weight="fill" className="text-alert-critical" />
          <span className="text-sm font-heading font-bold text-alert-critical">{unread.length}</span>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-surface border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Unread alerts */}
          {unread.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Unread</h3>
              {unread.map((alert, i) => {
                const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                const AlertIcon = config.icon;
                return (
                  <div
                    key={i}
                    data-testid={`alert-item-${i}`}
                    className={`flex items-start gap-3 p-4 rounded-lg border ${config.border} ${config.bg} transition-all duration-200`}
                  >
                    <AlertIcon size={20} weight="duotone" className={config.text} />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-medium text-text-primary">{alert.title}</p>
                      <p className="font-body text-xs text-text-secondary mt-0.5">{alert.message}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] font-body text-tertiary uppercase tracking-wider">{alert.site}</span>
                        <span className={`text-[10px] font-body font-semibold uppercase tracking-wider ${config.text}`}>
                          {alert.severity}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleMarkRead(alert.title)}
                      className="flex-shrink-0 p-2 rounded-lg hover:bg-white/50 transition-colors"
                      title="Mark as read"
                    >
                      <Eye size={16} className="text-text-secondary" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Read alerts */}
          {read.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Read</h3>
              {read.map((alert, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-lg border border-border bg-surface/50 opacity-60 transition-all"
                >
                  <CheckCircle size={20} weight="duotone" className="text-success" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-text-primary">{alert.title}</p>
                    <p className="font-body text-xs text-text-secondary mt-0.5">{alert.message}</p>
                    <span className="text-[10px] font-body text-tertiary uppercase tracking-wider mt-1 inline-block">{alert.site}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {alerts.length === 0 && (
            <div className="text-center py-12">
              <Bell size={48} weight="duotone" className="text-border mx-auto mb-3" />
              <p className="font-body text-sm text-text-secondary">No alerts for this industry</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
